import { useState, useCallback } from 'react'
import type { Square } from '../chessEngine'
import type { ChessDifficulty, MovementPosition } from '../chessData'
import { generateMovementPosition, PIECE_SYMBOLS } from '../chessData'
import type { SquareHighlight } from '../ChessBoard'
import ChessBoard from '../ChessBoard'
import { useTranslation } from '../../../i18n/LanguageContext'
import { useGameCompletion } from '../../../hooks/useGameCompletion'
import GamePlayHeader from '../../GamePlayHeader'
import SaveErrorBanner from '../../SaveErrorBanner'
import { COLORS } from '../../../styles/tokens'

interface Props {
  difficulty:  ChessDifficulty
  onCompleted: (stats: { moves: number; startTime: number | null; endTime: number | null; pairsTotal: number; pairsMatched: number }) => void
  onBack:      () => void
}

const ROUNDS_PER_GAME = 6

interface State {
  position:     MovementPosition
  round:        number
  markedSquares: Set<Square>
  submitted:    boolean
  correctCount: number
  wrongCount:   number
  answered:     number
  moves:        number
  startTime:    number | null
  endTime:      number | null
  gameComplete: boolean
}

function initState(difficulty: ChessDifficulty): State {
  return {
    position:      generateMovementPosition(difficulty),
    round:         1,
    markedSquares: new Set(),
    submitted:     false,
    correctCount:  0,
    wrongCount:    0,
    answered:      0,
    moves:         0,
    startTime:     null,
    endTime:       null,
    gameComplete:  false,
  }
}

export default function MovementMode({ difficulty, onCompleted, onBack }: Props) {
  const { t }   = useTranslation()
  const ch      = t.chess
  const [state, setState] = useState<State>(() => initState(difficulty))
  const [boardSize, setBoardSize] = useState(320)

  const handleBoardRef = useCallback((el: HTMLDivElement | null) => {
    if (!el) return
    const sz = Math.min(el.clientWidth, el.clientHeight, 480)
    setBoardSize(Math.floor(sz / 8) * 8)
    const ro = new ResizeObserver(() => {
      const s = Math.min(el.clientWidth, el.clientHeight, 480)
      setBoardSize(Math.floor(s / 8) * 8)
    })
    ro.observe(el)
  }, [])

  const toggleSquare = useCallback((sq: Square) => {
    setState((prev) => {
      if (prev.submitted || prev.gameComplete) return prev
      if (sq === prev.position.targetSquare) return prev
      const startTime  = prev.startTime ?? Date.now()
      const newMarked  = new Set(prev.markedSquares)
      if (newMarked.has(sq)) { newMarked.delete(sq) } else { newMarked.add(sq) }
      return { ...prev, markedSquares: newMarked, startTime }
    })
  }, [])

  const submitAnswer = useCallback(() => {
    setState((prev) => {
      if (prev.submitted || prev.gameComplete) return prev
      const valid   = new Set(prev.position.validSquares)
      const marked  = prev.markedSquares
      const allCorrect =
        marked.size === valid.size &&
        [...marked].every((sq) => valid.has(sq))

      const moves    = prev.moves + 1
      const answered = prev.answered + (allCorrect ? 1 : 0)
      return { ...prev, submitted: true, moves, answered,
        correctCount: allCorrect ? prev.correctCount + 1 : prev.correctCount,
        wrongCount:   allCorrect ? prev.wrongCount : prev.wrongCount + 1,
      }
    })
  }, [])

  const nextRound = useCallback(() => {
    setState((prev) => {
      const nextRound = prev.round + 1
      if (nextRound > ROUNDS_PER_GAME) {
        return { ...prev, gameComplete: true, endTime: Date.now() }
      }
      return {
        ...prev,
        position:      generateMovementPosition(difficulty),
        round:         nextRound,
        markedSquares: new Set(),
        submitted:     false,
      }
    })
  }, [difficulty])

  const { saveError, clearSaveError } = useGameCompletion({
    gameComplete:  state.gameComplete,
    activitySlug:  'chess',
    difficulty,
    mode:          'movement',
    moves:         state.moves,
    startTime:     state.startTime,
    endTime:       state.endTime,
    pairsTotal:    ROUNDS_PER_GAME,
    pairsMatched:  state.answered,
    onCompleted:   () => onCompleted({
      moves:        state.moves,
      startTime:    state.startTime,
      endTime:      state.endTime,
      pairsTotal:   ROUNDS_PER_GAME,
      pairsMatched: state.answered,
    }),
  })

  // Build highlight map
  const highlights: Partial<Record<Square, SquareHighlight>> = {
    [state.position.targetSquare]: 'selected',
  }
  if (state.submitted) {
    const valid = new Set(state.position.validSquares)
    // Show correct squares
    for (const sq of state.position.validSquares) {
      highlights[sq] = 'correct'
    }
    // Show wrongly marked squares
    for (const sq of state.markedSquares) {
      if (!valid.has(sq)) highlights[sq] = 'wrong'
    }
  } else {
    for (const sq of state.markedSquares) {
      highlights[sq] = 'marked'
    }
  }

  const { position } = state
  const pieceSymbol  = PIECE_SYMBOLS['w'][position.pieceType]
  const progress     = ((state.round - 1) / ROUNDS_PER_GAME) * 100

  return (
    <div style={s.root}>
      {saveError && <SaveErrorBanner message={t.common.saveError} dismissLabel={t.common.dismissError} onDismiss={clearSaveError} />}

      <GamePlayHeader
        title={ch.title}
        subtitle={ch.modes.movement.label}
        stats={[
          { value: `${state.answered}/${state.round - 1 + (state.submitted ? 1 : 0)}`, label: ch.movement.correct },
          { value: `${state.round}/${ROUNDS_PER_GAME}`, label: ch.round },
        ]}
        progress={progress}
        progressNow={state.round - 1}
        progressMax={ROUNDS_PER_GAME}
        onBack={onBack}
        backLabel={ch.backToDashboard}
      />

      <div style={s.content}>
        <p style={s.instruction}>
          {ch.movement.instruction.replace('{piece}', `${pieceSymbol} ${ch.recognize.pieces[position.pieceType]}`)}
        </p>

        <div ref={handleBoardRef} style={s.boardWrap}>
          <ChessBoard
            fen={position.fen}
            size={boardSize}
            highlights={highlights}
            onSquareClick={state.submitted ? undefined : toggleSquare}
            disabled={state.submitted}
          />
        </div>

        {!state.submitted ? (
          <button
            style={{ ...s.btn, backgroundColor: COLORS.greenDark }}
            onClick={submitAnswer}
            disabled={state.markedSquares.size === 0}
          >
            {ch.movement.submitBtn}
          </button>
        ) : (
          <div style={s.feedback}>
            <p style={{ ...s.feedbackText, color: state.correctCount > state.wrongCount ? '#2D6A4F' : '#c0392b' }}>
              {state.position.validSquares.length === [...state.markedSquares].filter(sq => state.position.validSquares.includes(sq)).length &&
               state.markedSquares.size === state.position.validSquares.length
                ? ch.movement.correct
                : ch.movement.wrong.replace('{n}', String(state.position.validSquares.length))}
            </p>
            <button style={{ ...s.btn, backgroundColor: COLORS.greenDark }} onClick={nextRound}>
              {state.round >= ROUNDS_PER_GAME ? ch.movement.finish : ch.movement.nextBtn}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

const s: Record<string, React.CSSProperties> = {
  root:        { height: '100dvh', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: COLORS.bgPage },
  content:     { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', padding: '0.75rem 1rem', overflowY: 'auto' },
  instruction: { fontSize: '1rem', color: '#333', fontStyle: 'italic', margin: 0, textAlign: 'center' },
  boardWrap:   { display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, width: '100%' },
  btn: {
    color: '#fff', border: 'none', borderRadius: 12, padding: '0.875rem 2rem',
    fontSize: '1rem', fontWeight: 700, cursor: 'pointer', minHeight: 48,
  },
  feedback:     { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' },
  feedbackText: { fontSize: '1.125rem', fontWeight: 700, margin: 0, textAlign: 'center' },
}
